import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type OrderEditingFlatTeamSettingGroupId =
  | "order_editing.editing_rules"
  | "order_editing.subscription_editing";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.orderEditing",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "medium",
  reason:
    "Changes whether cancelled or removed order items are returned to available inventory.",
};

export const OrderEditingFlatTeamSettingGroups = [
  {
    id: "order_editing.subscription_editing",
    availability: {
      conditions: [
        {
          type: "evaluator",
          evaluator: "integration.connected",
          integration: "recharge",
        },
      ],
      unavailableBehavior: "hidden",
      reason: "Connect Recharge to enable subscription editing.",
    },
    title: "Subscription Editing",
    description:
      "Choose which recurring subscription changes customers can make in the customer portal.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: {
      level: "medium",
      reason:
        "Enables customer changes to future recurring deliveries and charges.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management/order-editing",
    },
    synonyms: [],
  },
  {
    id: "order_editing.editing_rules",
    title: "Editing Rules",
    description:
      "Configure which order edits customers can make and whether inventory is restocked when items leave the order.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management/order-editing",
    },
    synonyms: [
      "order editing",
      "order editing rules",
      "order editing restock",
      "return inventory on cancel",
    ],
  },
] as const satisfies readonly SettingGroupDefinition<OrderEditingFlatTeamSettingGroupId>[];

type _assertAllOrderEditingFlatTeamSettingGroupIdsProvided =
  OrderEditingFlatTeamSettingGroupId extends (typeof OrderEditingFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allOrderEditingFlatTeamSettingGroupIdsProvided: _assertAllOrderEditingFlatTeamSettingGroupIdsProvided = true;
void _allOrderEditingFlatTeamSettingGroupIdsProvided;

export const OrderEditingFlatTeamSettingGroupById: Record<
  OrderEditingFlatTeamSettingGroupId,
  SettingGroupDefinition<OrderEditingFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      OrderEditingFlatTeamSettingGroupId,
      SettingGroupDefinition<OrderEditingFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of OrderEditingFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    OrderEditingFlatTeamSettingGroupId,
    SettingGroupDefinition<OrderEditingFlatTeamSettingGroupId>
  >;
})();
